import React from 'react';
import { Shield, Zap, Heart, Home, Phone, Wifi, Monitor, AlertTriangle, CheckCircle, ArrowRight } from 'lucide-react';

const LearnAboutEMF = () => {
  const emfSources = [
    { icon: <Phone className="w-6 h-6" />, name: "Cell phones", level: "High" },
    { icon: <Wifi className="w-6 h-6" />, name: "Wi-Fi routers", level: "Medium" },
    { icon: <Monitor className="w-6 h-6" />, name: "Computers", level: "Medium" },
    { icon: <Home className="w-6 h-6" />, name: "Smart appliances", level: "Low" }
  ];

  const protectionTips = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Distance is Key",
      description: "Keep devices away from your body when possible. Use speaker phone or wired headsets for calls.",
      color: "blue"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Limit Usage Time", 
      description: "Reduce the time spent on devices, especially during sleep hours and meal times.",
      color: "green"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Use Protection Products",
      description: "EMF blocking cases, shields, and other protection products can help reduce exposure levels.",
      color: "purple"
    },
    {
      icon: <Home className="w-8 h-8" />,
      title: "Create EMF-Free Zones",
      description: "Designate areas in your home, especially bedrooms, as low-EMF sanctuary spaces.",
      color: "orange"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Important Health Information
          </div>
          <h1 className="text-6xl font-bold bg-gradient-to-r from-gray-800 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Learn About EMF
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Understanding Electromagnetic Fields and their impact on your health and daily life
          </p>
        </div>

        {/* What is EMF Section */}
        <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-10 shadow-xl mb-12 border border-white/20">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center mr-4">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800">What is EMF?</h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <p className="text-gray-600 leading-relaxed mb-6 text-lg">
                Electromagnetic Fields (EMF) are invisible areas of energy, often referred to as radiation, 
                that are associated with the use of electrical power and various forms of natural and 
                man-made lighting.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg">
                There are two main categories: ionizing (high-frequency) and non-ionizing (low-frequency). 
                Most daily EMFs from electronic devices are non-ionizing, but research suggests 
                even these may have biological effects.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">EMF Frequency Ranges</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Radio Waves</span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">Low Risk</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Microwaves</span>
                  <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">Moderate</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">X-rays</span>
                  <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm">High Risk</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EMF Sources */}
        <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-10 shadow-xl mb-12 border border-white/20">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mr-4">
              <Monitor className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800">Common EMF Sources</h2>
          </div>

          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-6 mb-8">
            {emfSources.map((source, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="text-blue-500 mb-4">{source.icon}</div>
                <h3 className="font-semibold text-gray-800 mb-2">{source.name}</h3>
                <span className={`px-3 py-1 rounded-full text-sm ${
                  source.level === 'High' ? 'bg-red-100 text-red-800' :
                  source.level === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'
                }`}>
                  {source.level} EMF
                </span>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-blue-800 mb-4">Household Devices</h3>
              <ul className="space-y-3">
                {['Cell phones and smartphones', 'Wi-Fi routers and modems', 'Laptops and computers', 'Microwave ovens', 'Smart TVs and streaming devices', 'Baby monitors'].map((item, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <CheckCircle className="w-4 h-4 text-blue-500 mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-purple-800 mb-4">External Sources</h3>
              <ul className="space-y-3">
                {['Cell phone towers', 'Power lines', 'Radio and TV broadcast stations', 'Radar systems', 'Smart meters', 'Bluetooth devices'].map((item, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <CheckCircle className="w-4 h-4 text-purple-500 mr-3 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Health Effects */}
        <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-10 shadow-xl mb-12 border border-white/20">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-2xl flex items-center justify-center mr-4">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800">Potential Health Effects</h2>
          </div>
          
          <p className="text-gray-600 leading-relaxed mb-8 text-lg">
            While research is ongoing, some studies suggest that prolonged exposure to EMF radiation 
            may be associated with various health concerns:
          </p>
          
          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-red-600 mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-3" />
                Short-term Effects
              </h3>
              <ul className="space-y-4">
                {['Headaches', 'Sleep disruption', 'Fatigue', 'Difficulty concentrating', 'Skin irritation'].map((effect, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-red-400 rounded-full mr-4"></div>
                    {effect}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8">
              <h3 className="text-2xl font-semibold text-red-600 mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 mr-3" />
                Long-term Concerns
              </h3>
              <ul className="space-y-4">
                {['Increased cancer risk', 'Fertility issues', 'Immune system effects', 'Neurological disorders', 'Cardiovascular effects'].map((concern, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <div className="w-2 h-2 bg-red-500 rounded-full mr-4"></div>
                    {concern}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-6">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-yellow-600 mr-4 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">Important Research Note</h4>
                <p className="text-gray-700 leading-relaxed">
                  While some studies suggest these associations, the scientific community continues to research EMF effects. 
                  More comprehensive studies are needed to establish definitive causal relationships between EMF exposure and health outcomes.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Protection Tips */}
        <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-10 shadow-xl mb-12 border border-white/20">
          <div className="flex items-center mb-8">
            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mr-4">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-800">EMF Protection Strategies</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {protectionTips.map((tip, index) => (
              <div key={index} className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className={`w-16 h-16 bg-gradient-to-r ${
                  tip.color === 'blue' ? 'from-blue-500 to-cyan-500' :
                  tip.color === 'green' ? 'from-green-500 to-emerald-500' :
                  tip.color === 'purple' ? 'from-purple-500 to-pink-500' :
                  'from-orange-500 to-red-500'
                } rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <div className="text-white">{tip.icon}</div>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{tip.title}</h3>
                <p className="text-gray-600 leading-relaxed">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 rounded-3xl p-12 text-center text-white shadow-2xl">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Ready to Protect Yourself?</h2>
            <p className="text-xl mb-8 text-blue-100">
              Explore our scientifically-tested EMF protection products designed for your peace of mind and health.
            </p>
            <button className="group bg-white text-blue-600 px-10 py-4 rounded-2xl font-semibold hover:bg-gray-50 transition-all duration-300 hover:scale-105 shadow-xl flex items-center mx-auto">
              Shop EMF Protection Products
              <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnAboutEMF;